import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Moon, Sun, LogIn } from "lucide-react";
import AuthService from "../../services/AuthService";
import { AuthContext } from "../../context/AuthContext";
import Loading from "../../hooks/Loading";

const TopTabbar = () => {
    const [userName, setUserName] = useState("");
    const [userRole, setUserRole] = useState("");
    const [language, setLanguage] = useState("FR");
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { logout } = useContext(AuthContext);
    const navigate = useNavigate();

    const handleLogout = async () => {
        setIsLoading(true);
        try {
            await new Promise((resolve) => setTimeout(resolve, 3000));
            await logout();
            navigate("/login");
        } catch (error) {
            console.error("Erreur lors de la déconnexion :", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const user = AuthService.getCurrentUser();
        if (user) {
            setUserName(`${user.firstName}`);
            setUserRole(user.roleNames[0]);
        }

        const prefersDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
        const savedMode = localStorage.getItem("darkMode");
        const initialMode = savedMode !== null ? JSON.parse(savedMode) : prefersDarkMode;
        setIsDarkMode(initialMode);
        applyDarkMode(initialMode);
    }, []);

    const toggleDarkMode = () => {
        const newMode = !isDarkMode;
        setIsDarkMode(newMode);
        applyDarkMode(newMode);
        localStorage.setItem("darkMode", JSON.stringify(newMode));
    };

    const applyDarkMode = (isDark) => {
        if (isDark) {
            document.documentElement.classList.add("dark");
            document.body.classList.add("dark-mode");
        } else {
            document.documentElement.classList.remove("dark");
            document.body.classList.remove("dark-mode");
        }
    };

    if (isLoading) {
        return <Loading fullScreen={true} minDuration={3000} />;
    }

    return (
        <>
            <div className="top-tabbar">
                <div className="left-section">
                    <div className="logo-container">
                        <Link style={{marginLeft: 0}} to="/" className="logo-link">NTR Finance</Link>
                    </div>
                </div>

                <div className="right-section">
                    <Link to="/login">
                        <button className="login-button">
                            <LogIn className="login-icon" />
                            <span className="login-text">Connexion</span>
                        </button>
                    </Link>

                    <div className="user-settings">
                        <select
                            disabled
                            value={language}
                            onChange={(e) => setLanguage(e.target.value)}
                            className="language-selector"
                        >
                            <option value="FR">FR</option>
                            <option value="EN">EN</option>
                        </select>

                        <button
                            onClick={toggleDarkMode}
                            className="theme-toggle"
                            aria-label={isDarkMode ? "Activer le mode clair" : "Activer le mode sombre"}
                        >
                            {isDarkMode ? <Sun className="theme-icon" /> : <Moon className="theme-icon" />}
                        </button>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .top-tabbar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.8rem 2rem;
                    background-color: #294e88;
                    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    z-index: 1000;
                    height: 60px;
                }

                .logo-container {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 200px;
                }

                .logo-container .logo-link {
                    font-size: clamp(1.2rem, 3vw, 1.5rem);
                    font-weight: 700;
                    color: rgba(244, 244, 244, 0.85);
                    text-decoration: none;
                    font-family: 'Arial', sans-serif;
                    letter-spacing: 0.5px;
                    white-space: nowrap;
                }

                .right-section {
                    display: flex;
                    align-items: center;
                    gap: 1.5rem;
                }

                .login-button {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1.2rem;
                    background-color: #3498db;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .login-button:hover {
                    background-color: #2980b9;
                    transform: translateY(-1px);
                }

                .login-icon {
                    width: 18px;
                    height: 18px;
                }

                .login-text {
                    display: none;
                }

                .user-settings {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }

                .language-selector {
                    padding: 0.3rem 0.5rem;
                    border: 1px solid #e0e0e0;
                    border-radius: 4px;
                    background-color: transparent;
                    cursor: pointer;
                    font-size: 0.9rem;
                    outline: none;
                    color: #fff;
                }

                .theme-toggle {
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 0.3rem;
                    border-radius: 50%;
                    transition: all 0.3s ease;
                }

                .theme-toggle:hover {
                    background-color: rgba(0, 0, 0, 0.05);
                }

                .theme-icon {
                    width: 20px;
                    height: 20px;
                    color: #fff;
                }

                @media (min-width: 768px) {
                    .logo-container {
                        max-width: none;
                    }

                    .login-text {
                        display: inline;
                    }

                    .login-button {
                        padding: 0.6rem 1.5rem;
                    }

                    .login-icon {
                        width: 20px;
                        height: 20px;
                    }
                }

                /* Dark mode styles */
                /*.dark .top-tabbar {
                    background-color: #1a1a1a;
                    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
                }

                .dark .logo-container .logo-link {
                    color: #f8f9fa;
                }

                .dark .language-selector {
                    border-color: #444;
                    color: #f8f9fa;
                    background-color: #2d2d2d;
                }

                .dark .theme-icon {
                    color: #f8f9fa;
                }*/
            `}</style>
        </>
    );
};

export default TopTabbar;